// ═══════════════════════════════════════════════════════════════
// DonorDetailView — 2-Column Sidebar
// Path: src/components/donorDetail/DonorDetailSidebar.jsx
//
// Extracted from DonorDetailView.jsx during modularization Phase 4.
// The sticky left sidebar of the 2-column layout: identity card,
// engagement score, days since contact, key dates, classification,
// flags, committees, contact preferences, donor block, action buttons.
//
// Props-only — does not call useApp() directly.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from "react";
import { ListOrdered, ClipboardCheck } from "lucide-react";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { ActionButton } from "../ui/ActionButton";
import { DonorFlags } from "../donors/DonorParts";
import { DonorSidebarBlock } from "../features/DonorSidebarBlock";
import { supabase } from "../../lib/supabase";
import {
  ClassificationControls,
  FlagsCompact,
  CommitteeCheckboxes,
  ContactPreferencesCompact,
} from "./ClassificationSection";
import { RelationshipsPanel } from "./RelationshipsPanel";
import { DonorNotesPanel } from "./DonorNotesPanel";

// ═══════════════════════════════════════════════════════════════
// DonorDetailSidebar
// ═══════════════════════════════════════════════════════════════

export const DonorDetailSidebar = ({
  // Data
  detailForm,
  engagement,
  scoreColor,
  daysColor,
  scorePct,
  effective,
  palette,
  // Context data
  pathways,
  personas,
  directors,
  members,
  selectedMember,
  chapterSettings,
  // Permissions
  canEdit,
  canLogContact,
  canViewGifts,
  // Handlers
  updateField,
  updateMember,
  // Modal triggers
  onLogContact,
  onAddGift,
  onShowScoreAudit,
  onAddTask,
  navigate,
  showMessage,
}) => {
  // Compute "Donor Since" from first gift date if memberSince is empty
  const [firstGiftDate, setFirstGiftDate] = useState(null);
  useEffect(() => {
    if (detailForm?.memberSince || !selectedMember?.id) return;
    const load = async () => {
      const { data } = await supabase
        .from("gifts")
        .select("gift_date")
        .eq("member_id", selectedMember.id)
        .order("gift_date", { ascending: true })
        .limit(1);
      if (data?.[0]?.gift_date) setFirstGiftDate(data[0].gift_date);
    };
    load();
  }, [selectedMember?.id, detailForm?.memberSince]);

  const donorSinceValue = detailForm?.memberSince || firstGiftDate;
  const donorSinceDisplay = donorSinceValue
    ? new Date(donorSinceValue + "T12:00:00").toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null;

  return (
  <div
    style={{
      width: 310,
      minWidth: 310,
      overflowY: "auto",
      background: palette.cardBg,
      borderRight: `1px solid ${palette.cardBorder}`,
      display: "flex",
      flexDirection: "column",
    }}
  >
    {/* Identity */}
    <div
      style={{
        padding: "20px 18px 14px",
        textAlign: "center",
        borderBottom: `1px solid ${palette.cardBorder}`,
      }}
    >
      <div
        style={{
          width: 60,
          height: 60,
          borderRadius: "50%",
          background: effective.color,
          color: "#fff",
          fontSize: 20,
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 10px",
          boxShadow: `0 4px 12px ${effective.color}40`,
          fontFamily: FONT_FAMILY,
        }}
      >
        {detailForm.donorType === "organization"
          ? (detailForm.organization || "O").split(/\s+/).slice(0, 2).map(w => w[0]).join("")
          : detailForm.donorType === "household"
            ? (detailForm.householdName || `${detailForm.firstName || ""} ${detailForm.lastName || ""}`.trim() || "H").split(/\s+/).slice(0, 2).map(w => w[0]).join("")
            : `${detailForm.firstName?.[0] || ""}${detailForm.lastName?.[0] || ""}`
        }
      </div>
      <div
        style={{
          fontSize: FONT_SIZES.md,
          fontWeight: 700,
          color: palette.text1,
          fontFamily: FONT_FAMILY,
        }}
      >
        {detailForm.donorType === "organization"
          ? detailForm.organization || "Organization"
          : detailForm.donorType === "household"
            ? detailForm.householdName || `${detailForm.firstName || ""} ${detailForm.lastName || ""}`.trim() || "Household"
            : `${detailForm.firstName || ""} ${detailForm.lastName || ""}`.trim() || detailForm.credentialedFullName || "Donor"
        }
      </div>
      {(selectedMember?.spektrixId || selectedMember?.cpDonorId) && (
        <div
          style={{
            fontSize: FONT_SIZES.xs,
            color: palette.text2,
            fontFamily: FONT_FAMILY,
            marginTop: 4,
            fontWeight: 500,
            letterSpacing: "0.02em",
          }}
        >
          {selectedMember.spektrixId
            ? `Spektrix ID: ${selectedMember.spektrixId}`
            : `CP Donor ID: ${selectedMember.cpDonorId}`}
        </div>
      )}
      {detailForm.donorType === "organization"
        ? [detailForm.firstName, detailForm.lastName].filter(Boolean).join(" ") && (
            <div
              style={{
                fontSize: FONT_SIZES.sm,
                color: palette.text3,
                marginTop: 2,
              }}
            >
              {[detailForm.firstName, detailForm.lastName].filter(Boolean).join(" ")}
            </div>
          )
        : (
            <>
              {detailForm.title && (
                <div
                  style={{
                    fontSize: FONT_SIZES.sm,
                    color: palette.text3,
                    marginTop: 2,
                  }}
                >
                  {detailForm.title}
                </div>
              )}
              {detailForm.organization && (
                <div style={{ fontSize: FONT_SIZES.xs, color: palette.text3 }}>
                  {detailForm.organization}
                </div>
              )}
            </>
          )
      }
      <div style={{ marginTop: 8 }}>
        <DonorFlags member={detailForm} effective={effective} />
      </div>
    </div>

    {/* Engagement Score */}
    <div
      style={{
        padding: "12px 18px",
        borderBottom: `1px solid ${palette.cardBorder}`,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
        }}
      >
        <span
          style={{
            fontSize: FONT_SIZES.xs,
            fontWeight: 700,
            textTransform: "uppercase",
            color: palette.text3,
            letterSpacing: "0.06em",
          }}
        ></span>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              fontSize: 24,
              fontWeight: 800,
              color: scoreColor,
              fontFamily: FONT_FAMILY,
            }}
          >
            {engagement.score}
          </span>
          <button
            onClick={onShowScoreAudit}
            title="Score breakdown"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 2,
              color: palette.text3,
              display: "flex",
              borderRadius: 4,
            }}
          >
            <ListOrdered size={ICON_SIZES.sm} />
          </button>
        </div>
      </div>
      <div
        style={{
          height: 6,
          borderRadius: 3,
          background: "#f1f5f9",
          marginTop: 6,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${scorePct}%`,
            background: scoreColor,
            borderRadius: 3,
          }}
        />
      </div>
    </div>

    {/* Days Since Contact */}
    <div
      style={{
        padding: "12px 18px",
        borderBottom: `1px solid ${palette.cardBorder}`,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
        }}
      >
        <span
          style={{
            fontSize: FONT_SIZES.xs,
            fontWeight: 700,
            textTransform: "uppercase",
            color: palette.text3,
            letterSpacing: "0.06em",
          }}
        >
          Last Contact
        </span>
        <span
          style={{
            fontSize: 20,
            fontWeight: 800,
            color: daysColor,
            fontFamily: FONT_FAMILY,
          }}
        >
          {engagement.daysSinceContact === null
            ? "\u2014"
            : `${engagement.daysSinceContact}d`}
        </span>
      </div>
    </div>

    {/* Key Dates */}
    <div
      style={{
        padding: "12px 18px",
        borderBottom: `1px solid ${palette.cardBorder}`,
      }}
    >
      {[
        { label: "Donor Since", value: donorSinceDisplay },
        {
          label: "Last Contact",
          value: engagement.daysSinceContact !== null
            ? `${engagement.daysSinceContact} day${engagement.daysSinceContact !== 1 ? "s" : ""} ago`
            : null,
        },
        {
          label: "Days Since Donation",
          value: detailForm._daysSinceDonation,
        },
      ].map((row) => (
        <div
          key={row.label}
          style={{
            display: "flex",
            justifyContent: "space-between",
            padding: "4px 0",
          }}
        >
          <span
            style={{
              fontSize: FONT_SIZES.xs,
              fontWeight: 600,
              color: palette.text2,
            }}
          >
            {row.label}
          </span>
          <span style={{ fontSize: FONT_SIZES.sm, color: palette.text1 }}>
            {row.value || "\u2014"}
          </span>
        </div>
      ))}
    </div>

    {/* Classification Controls */}
    <div
      style={{
        padding: "12px 18px",
        borderBottom: `1px solid ${palette.cardBorder}`,
      }}
    >
      <ClassificationControls
        compact
        detailForm={detailForm}
        canEdit={canEdit}
        updateField={updateField}
        updateMember={updateMember}
        selectedMember={selectedMember}
        pathways={pathways}
        personas={personas}
        directors={directors}
        members={members}
        effective={effective}
        palette={palette}
      />
    </div>

    {/* Relationships */}
    <div
      style={{
        padding: "12px 18px",
        borderBottom: `1px solid ${palette.cardBorder}`,
      }}
    >
      <RelationshipsPanel
        selectedMember={selectedMember}
        members={members}
        canEdit={canEdit}
        palette={palette}
        navigate={navigate}
        showMessage={showMessage}
      />
    </div>

    {/* Flags */}
    <div
      style={{
        padding: "12px 18px",
        borderBottom: `1px solid ${palette.cardBorder}`,
      }}
    >
      <FlagsCompact
        detailForm={detailForm}
        canEdit={canEdit}
        updateField={updateField}
        updateMember={updateMember}
        selectedMember={selectedMember}
        effective={effective}
      />
    </div>

    {/* Contact Preferences (v1.3.0) */}
    <div
      style={{
        padding: "12px 18px",
        borderBottom: `1px solid ${palette.cardBorder}`,
      }}
    >
      <ContactPreferencesCompact
        detailForm={detailForm}
        canEdit={canEdit}
        updateField={updateField}
        updateMember={updateMember}
        selectedMember={selectedMember}
        palette={palette}
      />
    </div>

    {/* Donor Sidebar Block (Phase 3) */}
    {canViewGifts && (
      <div
        style={{
          padding: "12px 18px",
          borderBottom: `1px solid ${palette.cardBorder}`,
        }}
      >
        <DonorSidebarBlock
          member={selectedMember}
          palette={palette}
          canEdit={canEdit}
          onAddGift={onAddGift}
          detailForm={detailForm}
          updateField={updateField}
          updateMember={updateMember}
          chapterSettings={chapterSettings}
        />
      </div>
    )}

    {/* Donor Notes — pinned intelligence from contact reports (Phase 4) */}
    <DonorNotesPanel member={detailForm} palette={palette} />

    {/* Action Buttons */}
    <div
      style={{
        marginTop: "auto",
        padding: "14px 18px",
        borderTop: `1px solid ${palette.cardBorder}`,
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {canLogContact && (
          <ActionButton
            label="\u270F\uFE0F I made contact \u2014 log it"
            onClick={onLogContact}
            style={{ width: "100%" }}
          />
        )}
        {onAddTask && (
          <ActionButton
            label="Add Task"
            icon={ClipboardCheck}
            variant="secondary"
            onClick={onAddTask}
            small
          />
        )}
      </div>
    </div>
  </div>
  );
};
